import { useState, useEffect } from 'react';
import { assessmentService } from '../services/assessmentService';
import { caseService } from '../services/caseService';
import { VIEWS, FEEDBACK_THRESHOLD } from '../config/constants';
import { getFromStorage, saveToStorage, removeFromStorage } from '../utils/storage';

/**
 * Custom hook for assessment management with case management
 */
export const useAssessment = (authToken, onAuthError, onUsageUpdate, navigate) => {
  const [notes, setNotes] = useState('');
  const [results, setResults] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editedScore, setEditedScore] = useState(null);
  const [currentCase, setCurrentCase] = useState(null);
  const [cases, setCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [isSynced, setIsSynced] = useState(false);

  // Load cases from server on mount (with localStorage migration)
  useEffect(() => {
    const loadCases = async () => {
      if (!authToken) {
        // No auth token - load from localStorage as fallback
        const localCases = getFromStorage('cases') || [];
        setCases(localCases);
        setIsSynced(false);
        return;
      }

      setIsLoadingCases(true);
      try {
        // Fetch from server
        const serverCases = await caseService.getCases(authToken);
        setCases(serverCases);
        setIsSynced(true);

        // Check for localStorage data to migrate
        const localCases = getFromStorage('cases') || [];
        if (localCases.length > 0) {
          console.log(`Migrating ${localCases.length} local cases to server...`);
          await migrateLocalCasesToServer(localCases, authToken);
          removeFromStorage('cases');
          console.log('Migration complete! localStorage cleared.');
          
          // Reload from server after migration
          const updatedCases = await caseService.getCases(authToken);
          setCases(updatedCases);
        }
      } catch (error) {
        console.error('Failed to load cases from server:', error);
        
        // Fallback to localStorage if server fails
        const localCases = getFromStorage('cases') || [];
        setCases(localCases);
        setIsSynced(false);
      } finally {
        setIsLoadingCases(false);
      }
    };

    loadCases();
  }, [authToken]);

  /**
   * Migrate localStorage cases to server
   */
  const migrateLocalCasesToServer = async (localCases, token) => {
    const migrationPromises = localCases.map(async (caseItem) => {
      try {
        // Create case on server
        await caseService.createCase(caseItem.name, token);
        console.log(`Migrated case: ${caseItem.name}`);
      } catch (error) {
        console.error(`Failed to migrate case ${caseItem.name}:`, error);
      }
    });

    await Promise.all(migrationPromises);
  };

  /**
   * Analyze notes
   */
  const analyzeNotes = async () => {
    if (!authToken) {
      alert('Please login first');
      return;
    }

    navigate('/assessment/processing');

    try {
      // Include caseId if a case is selected
      const caseId = currentCase?.caseId || null;
      const data = await assessmentService.generateAssessment(notes, authToken, caseId);

      // Update usage info
      if (data.usage) {
        onUsageUpdate(data.usage);
      }

      // Start polling for results
      if (data.assessmentId) {
        pollForResults(data.assessmentId, data.usage);
      } else {
        throw new Error('No assessmentId received from server');
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        alert('Session expired. Please login again.');
        onAuthError();
      } else if (error.message.includes('429')) {
        alert('Usage limit reached. Please try again later.');
      } else {
        console.error('Error generating assessment:', error);
        alert('Failed to generate assessment. Please try again.');
      }
      navigate('/assessment');
    }
  };

  /**
   * Poll for assessment results
   */
  const pollForResults = (assessmentId, initialUsage) => {
    const onComplete = (data) => {
      const resultsData = {
        assessmentId: data.assessmentId,
        timestamp: data.updatedAt,
        results: data.results,
        usage: initialUsage,
        metadata: data.metadata
      };

      setResults(resultsData);
      navigate('/assessment/results');
    };

    const onError = (error) => {
      console.error('Polling error:', error);
      alert(error.message || 'Assessment failed. Please try again.');
      navigate('/assessment');
    };

    assessmentService.pollAssessmentStatus(assessmentId, authToken, onComplete, onError);
  };

  /**
   * Start editing an item
   */
  const startEditing = (category, itemId, currentScore) => {
    setEditingItem(`${category}-${itemId}`);
    setEditedScore(currentScore || '');
  };

  /**
   * Save edited score
   */
  const saveEdit = (category, itemId) => {
    setResults(prev => {
      const newResults = { ...prev };
      
      if (newResults.results[itemId]) {
        newResults.results[itemId].userScore = parseInt(editedScore);
      }
      
      return newResults;
    });
    
    setEditingItem(null);
    setEditedScore(null);
  };

  /**
   * Cancel editing
   */
  const cancelEdit = () => {
    setEditingItem(null);
    setEditedScore(null);
  };

  /**
   * Revert to AI score
   */
  const revertToAI = (category, itemId) => {
    setResults(prev => {
      const newResults = { ...prev };
      
      if (newResults.results[itemId]) {
        newResults.results[itemId].userScore = null;
      }
      
      return newResults;
    });
  };

  /**
   * Start new assessment
   */
  const startNewAssessment = () => {
    setNotes('');
    setResults(null);
    setCurrentCase(null);
    navigate('/assessment');
  };

  /**
   * Submit case initials and create new case (server-side)
   */
  const submitCaseInitials = async (initials) => {
    if (!authToken || !isSynced) {
      // Fallback to localStorage if not synced
    const caseId = `case_${Date.now()}`;
    const newCase = {
      caseId,
      name: initials,
      assessments: [],
      assessmentCount: 0,
      lastAssessmentDate: null,
      createdDate: new Date().toISOString()
    };
      setCases(prev => [...prev, newCase]);
      saveToStorage('cases', [...cases, newCase]);
      setCurrentCase(null);
      navigate('/cases');
      return;
    }

    try {
      // Create case on server (server generates caseId)
      const newCase = await caseService.createCase(initials, authToken);
    
      // Add to local state
    setCases(prev => [...prev, newCase]);
    
    // Clear current case and return to case list
    setCurrentCase(null);
    navigate('/cases');
    } catch (error) {
      console.error('Failed to create case:', error);
      alert('Failed to create case. Please try again.');
    }
  };

  /**
   * Select case from assessment form (without changing view)
   */
  const selectCaseForAssessment = (caseId) => {
    const caseItem = cases.find(c => c.caseId === caseId);
    if (caseItem) {
      setCurrentCase(caseItem);
    }
  };

  /**
   * Clear current case selection
   */
  const clearCurrentCase = () => {
    setCurrentCase(null);
  };

  /**
   * Save assessment result for case
   * Handles two scenarios:
   * 1. Case exists (caseId provided) - link assessment to case
   * 2. New case (caseId is null) - create case, then link assessment
   */
  const saveAssessmentForCase = async (caseName, caseId) => {
    if (!results) return;

    if (!authToken || !isSynced) {
      // Fallback to localStorage
    const assessment = {
      assessmentId: results.assessmentId,
      date: results.timestamp || new Date().toISOString(),
      notes: notes,
      results: results.results
    };

    setCases(prevCases => {
      const existingCaseIndex = prevCases.findIndex(
        c => c.caseId === caseId
      );

        let updatedCases;
      if (existingCaseIndex !== -1) {
        // Update existing case
          updatedCases = [...prevCases];
        const existingCase = updatedCases[existingCaseIndex];
        updatedCases[existingCaseIndex] = {
          ...existingCase,
          assessments: [...(existingCase.assessments || []), assessment],
          assessmentCount: (existingCase.assessmentCount || 0) + 1,
          lastAssessmentDate: assessment.date
        };
      } else {
        // Create new case
          const newCaseId = `case_${Date.now()}`;
          updatedCases = [...prevCases, {
            caseId: newCaseId,
          name: caseName,
          assessments: [assessment],
          assessmentCount: 1,
          lastAssessmentDate: assessment.date,
          createdDate: new Date().toISOString()
        }];
      }
        
        saveToStorage('cases', updatedCases);
        return updatedCases;
      });
      return;
    }

    try {
      let finalCaseId = caseId;

      // If no caseId provided, create new case first
      if (!finalCaseId) {
        console.log('Creating new case:', caseName);
        const newCase = await caseService.createCase(caseName, authToken);
        finalCaseId = newCase.caseId;
      }

      // Link assessment to case (new or existing)
      // Include edited results if any scores were modified by the clinician
      console.log(`Linking assessment ${results.assessmentId} to case ${finalCaseId}`);
      await assessmentService.linkAssessmentToCase(
        results.assessmentId,
        finalCaseId,
        authToken,
        results.results  // Pass the results which may contain userScore edits
      );

      // Refresh case list to show updated assessment counts
      const updatedCases = await caseService.getCases(authToken);
      setCases(updatedCases);
    } catch (error) {
      console.error('Failed to save assessment to case:', error);
      alert('Failed to save assessment to case. Please try again.');
    }
  };

  return {
    notes,
    setNotes,
    results,
    editingItem,
    editedScore,
    setEditedScore,
    currentCase,
    cases,
    isLoadingCases,
    isSynced,
    analyzeNotes,
    startEditing,
    saveEdit,
    cancelEdit,
    revertToAI,
    startNewAssessment,
    submitCaseInitials,
    selectCaseForAssessment,
    clearCurrentCase,
    saveAssessmentForCase
  };
};
